"use client";

import React from 'react';
import { motion } from 'framer-motion';

// --- Refactored & More Stable Animation Variants ---

// This variant is applied to parent containers to stagger the animation of their children.
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

// A simple fade-in-up animation for individual elements.
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// A slide-in animation that can come from the left or right.
const slideIn = (direction = 'left') => ({
  hidden: {
    x: direction === 'left' ? -100 : 100,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
    },
  },
});

// Variants for the word-by-word title animation (used for the initial page load).
const sentenceVariant = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.1,
    },
  },
};

const wordVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
};

const techFeatures = [
    { title: "Backend Powered by Node.js & Express", description: "Our modular backend API delivers secure, performant, and maintainable services that form the foundation of the platform.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg> },
    { title: "React-Based Agent Dashboard", description: "The frontend dashboard offers an intuitive, professional interface optimized for multi-tasking and efficiency.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg> },
    { title: "AI-Driven Customer Engagement", description: "Void’s AI assistant autonomously handles initial interactions — delivering fast responses and triaging requests to reduce agent workload.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg> },
    { title: "Data Storage Strategy", description: "We begin with MYSQL file-based storage for flexibility during development, with planned migration to MongoDB or PostgreSQL for enterprise scalability.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg> },
    { title: "Security with JWT & Role-Based Access Control", description: "All endpoints and socket connections are protected, ensuring secure, authorized access.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.1-.9 2-2 2s-2-.9-2-2 2-4 2-4 2 2.9 2 4zm0 0c0 1.1.9 2 2 2s2-.9 2-2-2-4-2-4-2 2.9-2 4zm-6 7v-2c0-2.2 3.6-4 6-4s6 1.8 6 4v2H6z" /></svg> },
    { title: "Seamless Platform Integration", description: "Connect effortlessly with your existing tools and CRMs to streamline workflows without disrupting your tech stack.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6h4M6 10v4m12-4v4M7.5 7.5l9 9M16.5 7.5l-9 9" /></svg> },
];

const expertisePoints = [
    { title: "Reliability", description: "Designing systems that perform consistently under load, with mechanisms to prevent data loss, such as persistent request queues." },
    { title: "Usability", description: "Crafting intuitive interfaces that reduce agent cognitive load and accelerate response times." },
    { title: "Transparency", description: "Ensuring every conversation is logged and auditable, empowering management with insights and compliance assurance." },
    { title: "Innovation", description: "Continuously integrating AI and automation to improve efficiency without sacrificing human touch." }
];

const differentiators = [
    { title: "Unified Customer Engagement Across Channels", description: "Eliminate fractured communication silos by integrating website chat, WhatsApp, and Messenger into a coherent workflow.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg> },
    { title: "AI-Augmented Support", description: "Intelligent bot manages initial contact autonomously — focusing agent time on high-value conversations.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg> },
    { title: "Role-Based Access with Full Administrative Oversight", description: "Secure user roles with comprehensive user management and conversation auditing.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.1-.9 2-2 2s-2-.9-2-2 2-4 2-4 2 2.9 2 4zm0 0c0 1.1.9 2 2 2s2-.9 2-2-2-4-2-4-2 2.9-2 4zm-6 7v-2c0-2.2 3.6-4 6-4s6 1.8 6 4v2H6z" /></svg> },
    { title: "Scalable & Future-Proof Architecture", description: "Designed to grow with your business from lightweight MYSQL storage to enterprise-grade databases.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg> }
];

export default function About() {
  return (
    <main className="font-sans text-gray-900 bg-white scroll-smooth overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative flex min-h-[60vh] items-center justify-center py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 z-0 overflow-hidden opacity-20">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover">
            <source src="/images/about.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1400 600" preserveAspectRatio="xMidYMid slice">
            <path fill="#16a085" d="M0,600L80,573.3C160,547,320,493,480,480C640,467,800,493,960,506.7C1120,520,1280,520,1360,520L1400,520L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" />
          </svg>
        </div>
        <motion.div 
          className="relative z-10 w-full max-w-7xl mx-auto text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 
            className="text-[2.5rem] leading-tight sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight"
            variants={sentenceVariant}
          >
            <span className="block">
              {"About Void Support System".split(" ").map((word, index) => (
                <motion.span key={index} className="inline-block" variants={wordVariant}>
                  {word} 
                </motion.span>
              ))}
            </span>
            <span className="block text-[#16a085] mt-2">
              {"Empowering Seamless Support".split(" ").map((word, index) => (
                <motion.span key={index} className="inline-block" variants={wordVariant}>
                  {word} 
                </motion.span>
              ))}
            </span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="mt-6 max-w-xl md:max-w-3xl mx-auto text-md sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
            Empowering support teams with unified, intelligent, and scalable customer service solutions.
          </motion.p>
          <motion.div variants={fadeInUp} className="mt-10">
            <motion.a
              href="/request-demo"
              className="inline-block bg-[#16a085] text-white font-semibold px-8 py-3 rounded-lg shadow-lg"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Discover Our Vision
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Our Vision */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl xl:max-w-screen-2xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
         
            <img src="/images/OurVision.png" alt="A stylized graphic representing our company vision for unified support" className="rounded-xl shadow-lg w-full transform hover:scale-[1.02] transition-transform duration-300"/>
            <div className="absolute inset-0 -z-10 opacity-20">
              <svg className="w-full h-full" viewBox="0 0 600 400"><circle cx="300" cy="200" r="150" fill="#16a085" fillOpacity="0.3" /><path d="M100 300 Q300 100 500 300" stroke="#16a085" strokeWidth="4" fill="none" /></svg>
            </div>
   
          <motion.div
            variants={slideIn('right')}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 tracking-tight">
              Our Vision
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              At Void, we understand that exceptional customer support is the cornerstone of lasting business success. In an increasingly connected world, customers demand fast, consistent, and personalized responses — regardless of channel or time of day.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Void was created to meet these demands head-on. Our mission is to empower support teams with a unified, intelligent platform that consolidates conversations from every channel into a single, manageable interface. By blending advanced AI with real-time human interaction, we help businesses deliver seamless customer experiences that drive loyalty and growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Technology */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl xl:max-w-screen-2xl mx-auto">
          <motion.h2 variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{once: true}} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Cutting-Edge Technology
          </motion.h2>
          
          {/* Mobile Carousel */}
          <div className="md:hidden flex overflow-x-auto space-x-4 snap-x snap-mandatory pb-4 scrollbar-hide -mx-4 px-4">
              {techFeatures.map((item, index) => (
                  <motion.div
                      key={index}
                      variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }}
                      className="snap-center flex-shrink-0 w-[80%] sm:w-[48%] bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col"
                  >
                      <div className="flex items-center mb-4">{item.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                  </motion.div>
              ))}
          </div>

          {/* Desktop Grid */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {techFeatures.map((item, index) => (
                  <motion.div key={index} variants={fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col">
                      <div className="flex items-center mb-4">{item.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                  </motion.div>
              ))}
          </motion.div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl xl:max-w-screen-2xl mx-auto">
          <motion.h2 variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{once: true}} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Our Expertise
          </motion.h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              variants={slideIn('left')}
              initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
              className="space-y-6"
            >
              <p className="text-lg text-gray-600 leading-relaxed">
                Void is the product of extensive experience in building scalable, secure, and user-centric communication platforms. Our team combines deep knowledge of backend engineering, frontend development, AI integration, and security to deliver a solution that meets real-world business needs.
              </p>
              <motion.ul variants={staggerContainer} className="space-y-4">
                {expertisePoints.map((item, index) => (
                  <motion.li key={index} variants={fadeInUp} className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#16a085] text-white rounded-full flex items-center justify-center text-lg font-bold group-hover:bg-[#13876b] transition-colors duration-300">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#16a085] transition-colors duration-300">{item.title}</h3>
                      <p className="mt-2 text-gray-600">{item.description}</p>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
            <motion.div
              variants={slideIn('right')}
              initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
              className="relative"
            >
              <img src="/images/OurExpertise-01.png" alt="A diagram showing interconnected expertise in reliability, usability, and innovation" className="rounded-xl shadow-lg w-full transform hover:scale-[1.02] transition-transform duration-300" />
              <div className="absolute inset-0 -z-10 opacity-20">
                <svg className="w-full h-full" viewBox="0 0 600 400"><rect x="100" y="200" width="100" height="200" fill="#16a085" fillOpacity="0.2" /><rect x="250" y="150" width="100" height="250" fill="#16a085" fillOpacity="0.4" /><rect x="400" y="250" width="100" height="150" fill="#16a085" fillOpacity="0.6" /><path d="M100 300 Q300 100 500 300" stroke="#16a085" strokeWidth="4" fill="none" /></svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Void Stands Apart */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl xl:max-w-screen-2xl mx-auto">
          <motion.h2 variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{once: true}} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Why Void Stands Apart
          </motion.h2>

          <div className="relative">
            <div className="absolute inset-0 -z-10 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 1400 400"><circle cx="700" cy="200" r="200" fill="#16a085" fillOpacity="0.3" /><path d="M200 300 Q700 100 1200 300" stroke="#16a085" strokeWidth="6" fill="none" /></svg>
            </div>
            
            {/* Mobile Carousel */}
            <div className="md:hidden flex overflow-x-auto space-x-4 snap-x snap-mandatory pb-4 scrollbar-hide -mx-4 px-4">
              {differentiators.map((item, index) => (
                <motion.div key={index} variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} className="snap-center flex-shrink-0 w-[80%] sm:w-[48%] bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col">
                  <div className="flex items-center mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
            
            {/* Desktop Grid */}
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} className="hidden md:grid md:grid-cols-2 gap-8">
              {differentiators.map((item, index) => (
                <motion.div key={index} variants={fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col">
                  <div className="flex items-center mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
          <motion.p variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-12 text-center text-lg font-semibold text-gray-900 max-w-3xl mx-auto">
            We are dedicated to transforming your support operations with reliability, security, and innovation.
          </motion.p>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="max-w-7xl xl:max-w-screen-2xl mx-auto text-center"
        >
          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-extrabold text-[#16a085] mb-12 tracking-tight">
            Our Commitment to Your Success
          </motion.h2>
          <div className="relative">
            <div className="absolute inset-0 -z-10 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 1400 400"><circle cx="700" cy="200" r="250" fill="#16a085" fillOpacity="0.2" /><path d="M300 300 Q700 100 1100 300" stroke="#16a085" strokeWidth="6" fill="none" /></svg>
            </div>
            <motion.p variants={fadeInUp} className="max-w-4xl mx-auto text-lg text-gray-600 leading-relaxed italic">
              Customer support is more than technology — it’s about relationships. Void delivers a platform that not only streamlines your operations but also enriches customer engagement, setting a new standard for professionalism and responsiveness.
            </motion.p>
            <motion.p variants={fadeInUp} className="mt-8 max-w-4xl mx-auto text-lg text-gray-600 leading-relaxed">
              Partner with Void to transform your support today and prepare your business for the challenges of tomorrow.
            </motion.p>
            <motion.div variants={fadeInUp} className="mt-10">
              <motion.a
                href="/request-demo"
                className="inline-block bg-[#16a085] text-white font-semibold px-8 py-3 rounded-lg shadow-lg"
                whileHover={{ y: -5, scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                Get Started with Void
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </main>
  );
}